import { IconButton, Stack, TextField, Typography } from '@mui/material';
import { Eye, EyeClosed } from '@phosphor-icons/react';
import { useState } from 'react';
import { z } from 'zod';

import { useAppForm } from '@/hooks/useAppForm';
import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';
import type { UpdateUserDto, User } from '@/modules/api/backend';
import { formScrollIntoViewOptions } from '@/utils/formScrollIntoViewOptions';

interface EditUserFormFormProps {
  initialValues: User;
  onSubmit: (values: UpdateUserDto) => Promise<void>;
}

export function EditProfileForm({ initialValues, onSubmit }: EditUserFormFormProps) {
  const [showPassword, setShowPassword] = useState(false);

  const form = useAppForm({
    defaultValues: {
      firstName: initialValues.firstName,
      lastName: initialValues.lastName,
      email: initialValues.email,
      password: '',
      passwordConfirm: '',
    },
    ...formScrollIntoViewOptions,
    onSubmit: async ({ value }) => {
      const submitData: UpdateUserDto = {
        firstName: value.firstName ?? undefined,
        lastName: value.lastName ?? undefined,
        email: value.email,
      };

      if (value.password && value.password.trim() !== '') {
        submitData.password = value.password;
      }

      form.reset();
      await onSubmit(submitData);
    },
  });

  useNavigationBlocker(form);

  return (
    <form
      id="profileForm"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Stack spacing={2}>
        <Typography variant="h2">User*in</Typography>

        <form.AppField
          name="firstName"
          validators={{
            onChange: z.string().nonempty(),
          }}
        >
          {(field) => <field.TextField label="Vorname" />}
        </form.AppField>

        <form.AppField
          name="lastName"
          validators={{
            onChange: z.string().nonempty(),
          }}
        >
          {(field) => <field.TextField label="Nachname" />}
        </form.AppField>

        <form.AppField
          name="email"
          validators={{
            onChange: z.email(),
          }}
        >
          {(field) => <field.TextField label="E-Mail" type="email" />}
        </form.AppField>

        <form.Field
          name="password"
          validators={{
            onChangeListenTo: ['firstName', 'lastName'],
            onChange: ({ value, fieldApi }) => {
              if (value && value.length > 0) {
                if (value.length < 8) {
                  return 'Mindestens 8 Zeichen erforderlich';
                }

                const firstName = fieldApi.form.getFieldValue('firstName')?.toLowerCase().trim();
                const lastName = fieldApi.form.getFieldValue('lastName')?.toLowerCase().trim();
                const passwordLower = value.toLowerCase();

                if (firstName && passwordLower.includes(firstName)) {
                  return 'Das Passwort darf nicht den Vornamen enthalten';
                }

                if (lastName && passwordLower.includes(lastName)) {
                  return 'Das Passwort darf nicht den Nachnamen enthalten';
                }
              }

              return false;
            },
          }}
        >
          {(field) => (
            <TextField
              fullWidth
              error={field.state.meta.errors.length > 0}
              helperText={field.state.meta.errors[0]}
              label="Passwort"
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.value)}
              type={showPassword ? 'text' : 'password'}
              value={field.state.value ?? ''}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword((prevState) => !prevState)}
                    title={showPassword ? 'Anzeigen' : 'Verstecken'}
                  >
                    {showPassword ? <EyeClosed /> : <Eye />}
                  </IconButton>
                ),
              }}
            />
          )}
        </form.Field>

        <form.Field
          name="passwordConfirm"
          validators={{
            onChangeListenTo: ['password'],
            onChange: ({ value, fieldApi }) => {
              const password = fieldApi.form.getFieldValue('password');

              if (value !== password) {
                return 'Passwörter stimmen nicht überein';
              }

              return undefined;
            },
          }}
        >
          {(field) => (
            <TextField
              fullWidth
              error={field.state.meta.errors.length > 0}
              helperText={field.state.meta.errors[0]}
              label="Passwort bestätigen"
              name={field.name}
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.value)}
              sx={{ marginBottom: 4 }}
              type={showPassword ? 'text' : 'password'}
              value={field.state.value}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    title={showPassword ? 'Anzeigen' : 'Verstecken'}
                  >
                    {showPassword ? <EyeClosed /> : <Eye />}
                  </IconButton>
                ),
              }}
            />
          )}
        </form.Field>
      </Stack>
    </form>
  );
}
